import { EmptyLive, Loader } from 'components/scommessaFallback';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { breakpoint, isHappybetSite } from 'utility/constant';
import { decodePathname, selectIsFilterChanged } from 'features/sport';
import { resetFilterAvvenimento, resetMenuManifestazioniFilters } from 'features/sport/sportSlice';
import { useAppDispatch, useTypedSelector } from 'lib/centralStore';
import { useGetLabelByKey, useGetLabelsByKeys } from 'hooks/useLingUI';
import { AppFragment } from 'enums/path-fragment';
import { AvailableStreamsCarousel } from 'components/availableStreamsCarousel';
import { BannerHTML5, enBannerHTML5 } from 'components/bannerHTML5';
import { Category } from 'types/categoryEnum';
import { DirettissimeCarousel } from 'components/direttissimeCarousel';
import { HeaderSticky } from 'components/headerSticky';
import { HeaderTitleWithSearch } from 'components/headerTitleWithSearch';
import { LiveDisciplinaPageType } from 'lib/types/page';
import { LiveFiltersModal } from 'features/live/liveFiltersModal';
import { LiveInEvidenza } from 'features/live/liveInEvidenza';
import { LiveInternalLayout } from 'features/live/liveInternalLayout';
import { NextEvents } from 'components/nextEvents';
import { ScommesseLayout } from 'features/scommesseLayout';
import { SportComposer } from 'features/sport/components/sportComposer';
import { SportTopNavigation } from 'features/sport/components/sportTopNavigation';
import { SportsCacheTemplateDisciplinaLiveDto } from 'types/swagger';
import { TabsWithAccordion } from 'components/tabsWithAccordion';
import { TabsWithAccordionType } from 'lib/datoCms/queries/getSeoData';
import { Taxes } from 'features/sport/components/sportTicket/sportTicketSlice';
import { errorLabelsUpdated } from 'features/carrello/carrelloSlice';
import { isTruthy } from 'utility/functions';
import { selectIsOpenCarrello } from 'features/carrello/carrelloSelectors';
import { selectIsSportSearchEnabled } from 'features/configuration/selectors';
import { useInfoTemplateLive } from 'features/sport/hooks';
import { useRouter } from 'next/router';
import useWindowSize, { enComparer } from 'hooks/useWindowSize';
import { AllCarrelloErrors } from 'lib/datoCms/types';

type Props = {
  taxes?: Taxes;
  seoData?: TabsWithAccordionType;
  allCarrelloErrors: Array<AllCarrelloErrors>;
  liveDisciplinaPage: LiveDisciplinaPageType;
};

const PageContent = ({ taxes, seoData, allCarrelloErrors, liveDisciplinaPage }: Props) => {
  const dispatch = useAppDispatch();
  const { asPath, isFallback } = useRouter();
  const { path, disciplina } = useMemo(() => {
    const path = decodePathname(asPath);
    const [_, disciplina] = path.split('/');
    return { path, disciplina };
  }, [asPath]);

  const refContainer = useRef<HTMLDivElement>(null);

  const sportHeaderStickyLabel = useGetLabelByKey('sport-header-sticky')!;

  const isOpen = useTypedSelector(selectIsOpenCarrello);

  const { id, visualComposer } = liveDisciplinaPage ?? {};

  const isSportSearchEnabled = useTypedSelector(selectIsSportSearchEnabled);

  const { templateLive, hasfilters, isScommessaLoading, isTemplateLoading } =
    useInfoTemplateLive<SportsCacheTemplateDisciplinaLiveDto>();

  const { doCheck } = useWindowSize();
  const [isMobile, isDesktopXl] = doCheck([
    { comparer: enComparer.less, size: breakpoint.md },
    { comparer: enComparer.greater, size: breakpoint.xl },
  ]);

  const [openModal, setOpenModal] = useState<boolean>(false);

  useEffect(() => {
    dispatch(errorLabelsUpdated(allCarrelloErrors));
  }, [dispatch, allCarrelloErrors]);

  const isFilterChanged = useTypedSelector(selectIsFilterChanged);
  const resetFilters = () => {
    dispatch(resetFilterAvvenimento());
    dispatch(resetMenuManifestazioniFilters());
  };

  const [lblFilter, lblReset, lblApply, lblStreaming] = useGetLabelsByKeys([
    'live-filters-title',
    'live-filters-reset',
    'live-filters-apply',
    'now-in-streaming',
  ]);
  const renderContent = () => {
    if (isFallback || isTemplateLoading) return null;

    if (templateLive) {
      const objContent = (
        <React.Fragment>
          <LiveInEvidenza
            title={disciplina === 'streaming' ? lblStreaming : undefined}
            isNotTopTemplate={false}
            removeTabs={disciplina !== 'streaming'}
            hasFilter={hasfilters}
            hasPaddingRight
            noPaddingRight={isMobile || isOpen || isDesktopXl}
            handleOpenFilters={setOpenModal}
          />
          {visualComposer && (
            <SportComposer
              key={`Composer-${path}`}
              pageId={id}
              cmsData={visualComposer}
              templateSlug={'templateLive'}
            />
          )}
        </React.Fragment>
      );

      const objFilters =
        hasfilters && openModal ? (
          <LiveFiltersModal
            key="live-disciplina-filters-placeholder"
            title={lblFilter}
            resetLabel={lblReset}
            dismissModalLabel={lblApply}
            setOn={setOpenModal}
          />
        ) : (
          <React.Fragment key="live-disciplina-filters-placeholder" />
        );

      return (
        <React.Fragment>
          {objContent}
          {objFilters}
        </React.Fragment>
      );
    }

    return null;
  };

  return (
    <ScommesseLayout taxes={taxes} hasNoPaddingOnMain>
      <HeaderTitleWithSearch
        title={'Sport'}
        category={Category.Sport}
        titleTrKey={AppFragment.Sport}
        labelSearch={isSportSearchEnabled ? 'Cerca' : null}
        hasPaddingLeft={isMobile}
        titleHighlighted="live"
        titleHighlightedTrKey={AppFragment.Live}
      />
      <SportTopNavigation hasFirstElementFixed={!isHappybetSite} isLive />
      <BannerHTML5 url={enBannerHTML5.Live} />
      <HeaderSticky label={sportHeaderStickyLabel} />
      <LiveInternalLayout>
        <div ref={refContainer} key={`container-${id}-${isTemplateLoading}-${isScommessaLoading}-${asPath}`}>
          {renderContent()}
          <Loader key={`loader-${id}-${isTemplateLoading}-${isScommessaLoading}-${asPath}`} />
        </div>
        {liveDisciplinaPage?.guardaSuSnaiBlock && (
          <AvailableStreamsCarousel
            title={liveDisciplinaPage.guardaSuSnaiBlock.title}
            className="inEvidenzaContainer"
            noPaddingRight={isMobile}
          />
        )}
        {liveDisciplinaPage?.direttissimeBlock && (
          <DirettissimeCarousel
            title={liveDisciplinaPage.direttissimeBlock.title}
            className="direttissimeContainer"
            slug={path}
            isEnabledPromo={liveDisciplinaPage.direttissimeBlock.promoEnabled}
          />
        )}
        <EmptyLive
          key={`empty-${id}-${isTemplateLoading}-${isScommessaLoading}-${asPath}`}
          container={refContainer.current}
          hasFilter={isFilterChanged}
          hasRedirect={!isTruthy(templateLive?.tabList?.length)}
          resetFilter={resetFilters}
        />
        {liveDisciplinaPage?.prossimiEventiBlock && (
          <NextEvents
            slug={path}
            title={liveDisciplinaPage.prossimiEventiBlock.title}
            noPaddingRight={isMobile || isOpen || isDesktopXl}
            isNotTopTemplate={false}
          />
        )}
        <TabsWithAccordion tabs={seoData?.tabs ?? []} />
      </LiveInternalLayout>
    </ScommesseLayout>
  );
};

export default PageContent;
